import { useState, useEffect, useRef } from "react";

export type AudioOnlyReason = "degraded_connection" | "avatar_unavailable" | null;

// What the banner should communicate at any point in the session
export type BannerState =
  | "pre_call_degraded"       // before session: poor connection detected
  | "mid_call_instability"    // in video mode: quality dropped (briefly, before switch)
  | "active_audio_connection" // in audio-only mode: reason is connection
  // NOTE: avatar_unavailable is handled silently — no toast shown to the user
  | null;

interface UseAudioOnlyModeProps {
  /** True if this is the user's very first simulation — must never start audio-only */
  firstScenario: boolean | null;
  /** Whether LiveKit is fully connected */
  isConnected: boolean;
  /** LiveKit ConnectionQuality string value, e.g. 'poor', 'lost', 'good' */
  connectionQuality: string;
  /** Whether a remote video track from the avatar has been received */
  hasVideoTrack: boolean;
  /** Server-side override: lock into audio-only for the entire session (LiveAvatar unavailable) */
  forceAudioOnly?: boolean;
}

export interface AudioOnlyModeResult {
  isAudioOnly: boolean;
  audioOnlyReason: AudioOnlyReason;
  bannerState: BannerState;
  /** Try to exit audio-only mode. User-initiated — does not respect the auto-lock. */
  tryVideo: () => void;
}

const POOR_QUALITIES = new Set(["poor", "lost"]);
/** How long to wait for an avatar video track before declaring avatar unavailable */
const AVATAR_VIDEO_TIMEOUT_MS = 20_000;

/**
 * Manages the audio-only fallback mode for the call screen.
 *
 * Rules (from spec):
 * - Default is always video
 * - Audio-only is system-triggered only
 * - First-ever session must never start audio-only
 * - Once in audio-only, the system will NOT auto-switch back to video
 * - Users can manually attempt video via tryVideo()
 */
export function useAudioOnlyMode({
  firstScenario,
  isConnected,
  connectionQuality,
  hasVideoTrack,
  forceAudioOnly = false,
}: UseAudioOnlyModeProps): AudioOnlyModeResult {
  const isFirstEver = firstScenario === true;
  const isPoor = POOR_QUALITIES.has(connectionQuality?.toLowerCase?.() ?? "");

  const [isAudioOnly, setIsAudioOnly] = useState(forceAudioOnly);
  const [audioOnlyReason, setAudioOnlyReason] = useState<AudioOnlyReason>(
    forceAudioOnly ? "avatar_unavailable" : null
  );
  const [bannerState, setBannerState] = useState<BannerState>(null);

  // Ref tracks whether we've locked into audio-only — prevents auto-revert to video
  const audioOnlyLockedRef = useRef(forceAudioOnly);
  // Ref tracks whether the session has started (connected at least once)
  const sessionStartedRef = useRef(false);

  const activateAudioOnly = (reason: AudioOnlyReason) => {
    if (isFirstEver) return; // Hard rule: first-ever must never start in audio-only
    audioOnlyLockedRef.current = true;
    setIsAudioOnly(true);
    setAudioOnlyReason(reason);
    // Avatar-unavailable is silent — no toast. Connection issues show a toast.
    setBannerState(reason === "avatar_unavailable" ? null : "active_audio_connection");
  };

  // ── Pre-call: show degraded banner before session starts ─────────────────
  useEffect(() => {
    if (sessionStartedRef.current) return; // session already started, not pre-call
    if (!isConnected && isPoor && !isFirstEver) {
      setBannerState("pre_call_degraded");
    } else if (!isConnected && !isPoor) {
      setBannerState(null);
    }
  }, [isPoor, isConnected, isFirstEver]);

  // ── Session start: resolve mode based on connection quality at that moment ─
  useEffect(() => {
    if (!isConnected || sessionStartedRef.current) return;
    sessionStartedRef.current = true;

    if (!isFirstEver && isPoor) {
      activateAudioOnly("degraded_connection");
    } else {
      // Session starts in video mode — clear any pre-call banner
      setBannerState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]); // Intentionally only tracks connection transition

  // ── Avatar unavailable: connected but no video within timeout ────────────
  useEffect(() => {
    if (!isConnected || hasVideoTrack) return;

    const timer = setTimeout(() => {
      if (!hasVideoTrack && !audioOnlyLockedRef.current) {
        // Silently fall back to audio-only — no toast for avatar unavailability
        activateAudioOnly("avatar_unavailable");
      }
      // If already audio-only, nothing to do — stay as-is, no banner update
    }, AVATAR_VIDEO_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, hasVideoTrack]);

  // (No avatar_unavailable banner to clear — it was removed)

  // ── Mid-call: quality degrades during a video session → switch to audio-only
  useEffect(() => {
    if (!sessionStartedRef.current || !isConnected) return;
    if (audioOnlyLockedRef.current) return; // already audio-only, do nothing
    if (isFirstEver) return;

    if (isPoor) {
      // Brief banner showing instability before switching
      setBannerState("mid_call_instability");
      // Small delay so the user sees the banner before mode switches
      const switchTimer = setTimeout(() => activateAudioOnly("degraded_connection"), 1_500);
      return () => clearTimeout(switchTimer);
    } else if (bannerState === "mid_call_instability") {
      setBannerState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPoor, isConnected]);

  // ── Try Video: user-initiated — breaks the auto-lock ──────────────────────
  // No-op when forceAudioOnly: there is no avatar video to switch back to.
  const tryVideo = () => {
    if (forceAudioOnly) return;
    audioOnlyLockedRef.current = false;
    sessionStartedRef.current = false; // allow re-evaluation on next connection
    setIsAudioOnly(false);
    setAudioOnlyReason(null);
    setBannerState(null);
  };

  // ── TEMP DEBUG params — remove before merging ────────────────────────────
  // ?audioOnly=true          → audio-only mode (connection reason)
  // ?audioOnly=avatar        → audio-only mode (avatar unavailable reason)
  // ?banner=pre_call         → pre-call degraded banner only (video mode)
  // ?banner=mid_call         → mid-call instability banner only (video mode)
  const debugParam = new URLSearchParams(window.location.search).get("audioOnly");
  const bannerParam = new URLSearchParams(window.location.search).get("banner");

  if (debugParam === "true") {
    return { isAudioOnly: true, audioOnlyReason: "degraded_connection" as AudioOnlyReason, bannerState: "active_audio_connection" as BannerState, tryVideo };
  }
  if (debugParam === "avatar") {
    // Avatar-unavailable is now silent — audio-only with no toast
    return { isAudioOnly: true, audioOnlyReason: "avatar_unavailable" as AudioOnlyReason, bannerState: null, tryVideo };
  }
  if (bannerParam === "pre_call") {
    return { isAudioOnly: false, audioOnlyReason: null, bannerState: "pre_call_degraded" as BannerState, tryVideo };
  }
  if (bannerParam === "mid_call") {
    return { isAudioOnly: false, audioOnlyReason: null, bannerState: "mid_call_instability" as BannerState, tryVideo };
  }
  // ── End debug params ──────────────────────────────────────────────────────

  return { isAudioOnly, audioOnlyReason, bannerState, tryVideo };
}
